import {
  useActivateGlowUpProduct,
  useDeactivateGlowUpProduct,
  useDeleteGlowUpProduct,
} from '@/api/glow-up-product/glow-up-product.hooks';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { getS3Url } from '@/config/s3.config';
import { GlowUpProduct } from '@repo/types/src/glow-up-product';
import { Trash } from 'lucide-react';
import { toast } from 'sonner';
import { AddGlowUpProduct } from './AddGlowUpProduct';

interface Props {
  product: GlowUpProduct;
}

export const GlowUpProductCard = ({ product }: Props) => {
  const activateMutation = useActivateGlowUpProduct();
  const deactivateMutation = useDeactivateGlowUpProduct();
  const deleteMutation = useDeleteGlowUpProduct();

  const handleActivate = () => {
    activateMutation.mutate(product.id, {
      onSuccess: () => toast.success('Produit activé !', { duration: 3000 }),
      onError: () =>
        toast.error("Erreur lors de l'activation", { duration: 3000 }),
    });
  };

  const handleDelete = () => {
    deleteMutation.mutate(product.id, {
      onSuccess: () =>
        toast.success('Produit supprimé !', { duration: 3000 }),
      onError: () =>
        toast.error('Erreur lors de la suppression', { duration: 3000 }),
    });
  };

  return (
    <Card className="overflow-hidden w-72 h-fit">
      <CardContent className="p-0 aspect-[4/3] w-full relative">
        <img
          src={getS3Url(product.image)}
          alt={product.title}
          className="object-cover w-full h-full"
        />
        <div className="absolute top-2 left-2 flex gap-1">
          <Badge variant="secondary">#{product.position}</Badge>
          {product.active && <Badge className="bg-green-600">Actif</Badge>}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start gap-2 pt-3 pb-3">
        <p className="text-sm font-semibold text-zinc-800">{product.title}</p>
        {product.description && (
          <p className="text-xs text-zinc-500 line-clamp-2">
            {product.description}
          </p>
        )}
        <p className="text-xs text-zinc-400">
          CTA : {product.ctaText} · Catégorie ID: {product.categoryId}
        </p>
        <div className="flex gap-2 w-full justify-end">
          {product.active ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                deactivateMutation.mutate(product.id, {
                  onSuccess: () =>
                    toast.success('Produit désactivé !', { duration: 3000 }),
                  onError: () =>
                    toast.error('Erreur lors de la désactivation', {
                      duration: 3000,
                    }),
                })
              }
            >
              Désactiver
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={handleActivate}>
              Activer
            </Button>
          )}
          <AddGlowUpProduct product={product} categoryId={product.categoryId} />
          <Button size="icon" variant="ghost" onClick={handleDelete}>
            <Trash size={16} color="red" />
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};
